import * as React from 'react';

import { DateTime } from 'luxon';

interface IDateTimeWithTimezoneProps {
  value?: Date;
  onChange: (value: Date | undefined) => void;
  timezone: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * A date and time input component that handles timezone conversion.
 *
 * @param props.value - The value as a Date object
 * @param props.onChange - Callback when the value changes
 * @param props.timezone - The timezone to display/convert dates in
 * @param props.min - Minimum date in YYYY-MM-DD format
 * @param props.max - Maximum date in YYYY-MM-DD format
 * @param props.disabled - Whether the input is disabled
 */
export const DateTimeWithTimezone: React.FC<IDateTimeWithTimezoneProps> = ({
  value,
  onChange,
  timezone,
  min,
  max,
  disabled,
  id,
}) => {
  const [localDate, setLocalDate] = React.useState<string>('');
  const [localTime, setLocalTime] = React.useState<string>('');

  // Update local state when value or timezone changes
  React.useEffect(() => {
    if (value && timezone) {
      const dt = DateTime.fromJSDate(value).setZone(timezone);
      setLocalDate(dt.toFormat('yyyy-MM-dd'));
      setLocalTime(dt.toFormat('HH:mm'));
    } else {
      setLocalDate('');
      setLocalTime('');
    }
  }, [value, timezone]);

  const handleChange = React.useCallback(
    (newDate: string, newTime: string) => {
      if (newDate && newTime && timezone) {
        const dt = DateTime.fromFormat(`${newDate} ${newTime}`, 'yyyy-MM-dd HH:mm', { zone: timezone });
        if (dt.isValid) {
          onChange(dt.toJSDate());
        }
      }
    },
    [timezone, onChange],
  );

  const handleDateChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = event.target.value;
      setLocalDate(newDate);
      handleChange(newDate, localTime);
    },
    [localTime, handleChange],
  );

  const handleTimeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = event.target.value;
      setLocalTime(newTime);
      handleChange(localDate, newTime);
    },
    [localDate, handleChange],
  );

  return (
    <div id={id} className="form-inline">
      <input
        type="date"
        className="form-control"
        value={localDate}
        onChange={handleDateChange}
        min={min}
        max={max}
        disabled={disabled}
      />
      <span> - </span>
      <input type="time" className="form-control" value={localTime} onChange={handleTimeChange} disabled={disabled} />
    </div>
  );
};
